import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Snackbar } from '@mui/material';
import { useAuth } from '../../../../context/AuthContext';
import { useScannerState } from './hooks/useScannerState';
import { useScanCapture } from './hooks/useScanCapture';
import ScannerHeader from './ScannerHeader';
import ScanPhase from './phases/ScanPhase';
import LoadingPhase from './phases/LoadingPhase';
import ResultPhase from './phases/ResultPhase';
import ErrorPhase from './phases/ErrorPhase';
import NotFoundPhase from './phases/NotFoundPhase';
import CreateItemPhase from './phases/CreateItemPhase';
import QuantityPhase from './phases/QuantityPhase';
import AddLotPhase from './phases/AddLotPhase';
import MsdsDrawer from './drawers/MsdsDrawer';
import LocationChip from './components/LocationChip';

// Undo lives on the booking's row in the recent list, not in the toast
const BOOKED_TOAST_MS = 4000;
const ALL_LOCATIONS = 'all';

/**
 * Where the scanner is working right now. Starts at the scanner's own
 * location; a change is kept on the device (storageKey) until changed again,
 * so a phone carried to the fridge stays there across scans and reloads.
 * null means all locations.
 */
function useWorkingLocation(defaultLocationId, storageKey, locations) {
  const [locationId, setLocationId] = useState(() => {
    const stored = storageKey && localStorage.getItem(storageKey);
    if (stored === ALL_LOCATIONS) return null;
    return stored || defaultLocationId || null;
  });

  // A stored location that has since been deactivated falls back to the default
  useEffect(() => {
    if (locationId && locations.length > 0 && !locations.some(l => l.stock_location_id === locationId)) {
      setLocationId(defaultLocationId && locations.some(l => l.stock_location_id === defaultLocationId) ? defaultLocationId : null);
    }
  }, [locationId, locations, defaultLocationId]);

  const change = (id) => {
    setLocationId(id);
    if (storageKey) localStorage.setItem(storageKey, id ?? ALL_LOCATIONS);
  };
  return [locationId, change];
}

/**
 * The phone scanner. Used by staff from the normal app (/inventory/scanner)
 * and by phones logged in as a scanner (/scanner), which pass their own
 * header, default location and permissions.
 */
export function ScannerView({
  header, defaultLocationId = null, locationStorageKey = null, staffSignedIn = true,
  canCreateItem, canCreateLot, canOpenFiles, canReadMsds = true,
}) {
  const { t } = useTranslation(['inventory', 'common']);
  const s = useScannerState();
  const [locationId, setLocationId] = useWorkingLocation(defaultLocationId, locationStorageKey, s.allLocations);
  const workingLocation = s.allLocations.find(l => l.stock_location_id === locationId) ?? null;

  // A hardware scan works from any screen, even when the barcode field has lost
  // focus (e.g. after tapping undo). Keys typed into a text field are left alone.
  useScanCapture(s.handleBarcode, { enabled: !['loading', 'create_item'].includes(s.phase) });

  const toastEntry = s.toast?.entry;

  return (
    <Box sx={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {header}

      {['scanning', 'result'].includes(s.phase) && s.allLocations.length > 0 && (
        <Box sx={{ px: 2, pt: 1.5 }}>
          <LocationChip locations={s.allLocations} value={locationId} onChange={setLocationId} />
        </Box>
      )}

      {s.phase === 'scanning' && (
        <ScanPhase
          inputRef={s.inputRef}
          inputValue={s.inputValue}
          onChange={s.setInputValue}
          onSubmit={s.handleBarcode}
          recent={s.recent}
          onUndo={s.undo}
        />
      )}

      {s.phase === 'loading' && <LoadingPhase />}

      {s.phase === 'result' && (
        <ResultPhase
          item={s.item}
          matchedLot={s.matchedLot}
          stockLevels={s.stockLevels}
          totalStock={s.totalStock}
          workingLocation={workingLocation}
          onPickLocation={setLocationId}
          expiryDate={s.expiryDate}
          expired={s.expired}
          isHazardous={s.itemHazardous}
          hazardLevel={s.item?.hazard_level}
          onScanAgain={s.resetScanner}
          onUseStock={s.goToUseStock}
          onAddStock={s.goToAddStock}
          onOpenMsds={canReadMsds ? s.handleOpenMsds : undefined}
        />
      )}

      {(s.phase === 'use_stock' || s.phase === 'add_stock') && (
        <QuantityPhase
          key={s.phase}
          direction={s.phase === 'use_stock' ? 'use' : 'add'}
          item={s.item}
          matchedLot={s.matchedLot}
          lots={s.lots}
          lotTracked={s.itemLotTracked}
          stockLevels={s.stockLevels}
          allLocations={s.allLocations}
          defaultLocationId={locationId}
          saving={s.saving}
          error={s.actionError}
          onConfirm={opts => s.book({ direction: s.phase === 'use_stock' ? 'use' : 'add', ...opts })}
          onCancel={s.backToResult}
        />
      )}

      {s.phase === 'add_lot' && (
        <AddLotPhase
          item={s.item}
          gs1Data={s.gs1Data}
          saving={s.saving}
          error={s.actionError}
          canCreate={canCreateLot}
          staffSignedIn={staffSignedIn}
          defaultLocationId={locationId}
          onConfirm={s.handleAddLot}
          onCancel={s.resetScanner}
        />
      )}

      {s.phase === 'error' && <ErrorPhase message={s.errorMessage} onReset={s.resetScanner} />}

      {s.phase === 'not_found' && (
        <NotFoundPhase
          barcode={s.scannedBarcode}
          canCreate={canCreateItem}
          onNo={s.resetScanner}
          onYes={s.startCreateItem}
        />
      )}

      {s.phase === 'create_item' && (
        <CreateItemPhase
          productGroups={s.productGroups}
          values={s.createValues}
          errors={s.createErrors}
          error={s.createError}
          saving={s.saving}
          onChange={s.handleCreateValueChange}
          onSave={s.handleCreateItem}
          onCancel={s.resetScanner}
        />
      )}

      <MsdsDrawer
        open={s.msdsOpen}
        msds={s.msds}
        msdsLoading={s.msdsLoading}
        canOpenFile={canOpenFiles}
        onClose={() => s.setMsdsOpen(false)}
      />

      <Snackbar
        open={Boolean(s.toast)}
        autoHideDuration={toastEntry ? BOOKED_TOAST_MS : 3000}
        onClose={(_, reason) => reason !== 'clickaway' && s.closeToast()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          data-testid="scanner-toast"
          severity={s.toast?.severity ?? 'success'}
          variant="filled"
          sx={{ width: '100%', alignItems: 'center' }}
        >
          {toastEntry
            ? t(toastEntry.direction === 'use' ? 'inventory:scanner.booked_use' : 'inventory:scanner.booked_add', {
              count: toastEntry.qty, uom: toastEntry.uom, item: toastEntry.itemName,
            })
            : s.toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// Staff member who opened the scanner from the normal app
export default function ScannerPage() {
  const { hasScope } = useAuth();
  return (
    <ScannerView
      header={<ScannerHeader />}
      locationStorageKey="orbit_scanner_location_staff"
      canCreateItem={hasScope('inventory:item:create')}
      canCreateLot={hasScope('inventory:lot:create')}
      canReadMsds={hasScope('inventory:msds:read')}
      canOpenFiles
    />
  );
}
